/**
 * Simple Load More Handler - Direct Implementation
 * Bypasses facet-link and handles AJAX pagination directly
 */

class ShopifyLoadMoreHandler {
  constructor() {
    this.isLoading = false;
    this.currentPage = 1;
    this.skeletonCardsCount = 8; // Default number of skeleton cards to show
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupLoadMoreButtons();
  }

  setupLoadMoreButtons() {
    // Remove facet-link interference by replacing buttons
    const facetLinks = document.querySelectorAll('facet-link');
    facetLinks.forEach(facetLink => {
      const button = facetLink.querySelector('[data-load-more-button]');
      if (button) {
        // Clone button outside of facet-link
        const newButton = button.cloneNode(true);
        facetLink.parentNode.replaceChild(newButton, facetLink);
      }
    });

    // Now set up our own click handlers
    const buttons = document.querySelectorAll('[data-load-more-button]');

    buttons.forEach((button, index) => {
      // Remove any existing listeners
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);

      // Style the button
      this.styleLoadMoreButton(newButton);

      // Check if infinite scroll is enabled
      const isInfiniteScroll = newButton.closest('[data-infinite-scroll="true"]');

      // Add our click handler (always works as fallback)
      newButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await this.handleLoadMore(newButton);
      });

      // If infinite scroll, setup intersection observer
      if (isInfiniteScroll) {
        // Hide button initially or style as loading spinner if desired
        // newButton.style.opacity = '0'; 

        const observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && !this.isLoading) {
            this.handleLoadMore(newButton);
          }
        }, {
          rootMargin: '1000px' // High threshold for seamless preload
        });

        observer.observe(newButton);

        // Store observer to disconnect later if needed (simple implementation attached to element)
        newButton._observer = observer;
      }
    });
  }

  createSkeletonCard() {
    const skeletonHTML = `
      <product-card class="product-card product-card--skeleton">
        <div class="product-card__figure">
          <div class="product-card__media">
            <div class="product-card__image product-card__image--primary skeleton-box aspect-square"></div>
          </div>
        </div>
        <div class="product-card__info">
          <div class="v-stack justify-items-start gap-2">
            <div class="v-stack justify-items-start gap-1">
              <div class="skeleton-title"></div>
              <div class="skeleton-price-list"></div>
            </div>
            <div class="skeleton-swatches h-stack gap-1">
              <div class="skeleton-swatch"></div>
            </div>
          </div>
        </div>
      </product-card>
    `;

    const template = document.createElement('template');
    template.innerHTML = skeletonHTML.trim();
    return template.content.firstChild;
  }

  addSkeletonCards(container, count = this.skeletonCardsCount) {
    const skeletonContainer = document.createElement('div');
    skeletonContainer.classList.add('skeleton-cards-container');

    // Get computed styles from the container to match grid layout
    const containerStyles = window.getComputedStyle(container);
    skeletonContainer.style.cssText = `
      display: grid;
      grid-template-columns: ${containerStyles.gridTemplateColumns};
      gap: ${containerStyles.gap};
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    `;

    // Add skeleton cards
    for (let i = 0; i < count; i++) {
      skeletonContainer.appendChild(this.createSkeletonCard());
    }

    // Add to container
    container.appendChild(skeletonContainer);

    return skeletonContainer;
  }

  removeSkeletonCards(container) {
    const skeletonContainer = container.querySelector('.skeleton-cards-container');
    if (skeletonContainer) {
      skeletonContainer.remove();
    }
  }

  async handleLoadMore(button) {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    const originalText = button.textContent;
    button.textContent = 'Loading...';
    button.disabled = true;

    // Find and hide the pagination container with smooth transition
    const paginationContainer = document.querySelector('[data-load-more-pagination]');
    if (paginationContainer) {
      paginationContainer.style.opacity = '0';
      paginationContainer.style.visibility = 'hidden';
      paginationContainer.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';
    }

    // Find the product container and add skeleton cards
    const container = this.findProductContainer();
    let skeletonContainer = null;

    if (container) {
      // Get the number of products per row to show appropriate skeleton count
      const firstRow = container.children[0];
      if (firstRow) {
        const containerWidth = container.offsetWidth;
        const itemWidth = firstRow.offsetWidth;
        const itemsPerRow = Math.floor(containerWidth / itemWidth) || 4; // Default to 4 if calculation fails
        const rowsToShow = 2; // Show 2 rows of skeletons
        this.skeletonCardsCount = Math.min(itemsPerRow * rowsToShow, 12); // Cap at 12 skeleton cards
      }

      // Add skeleton cards
      skeletonContainer = this.addSkeletonCards(container);
    }

    try {
      const nextUrl = button.getAttribute('href');

      // Fetch the next page
      const response = await fetch(nextUrl);
      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find products in the response
      if (!container) {
        throw new Error('Product container not found');
      }

      // Get the product container from the response
      const responseContainer = this.findProductContainerInDoc(doc);
      if (!responseContainer) {
        throw new Error('No product container in response');
      }

      const newProducts = Array.from(responseContainer.children);

      if (newProducts.length === 0) {
        throw new Error('No products found in response');
      }

      // Store current products before modifying
      const currentProducts = Array.from(container.children).filter(child => !child.classList.contains('skeleton-cards-container'));
      const currentProductHandles = this.getCurrentProductHandles();

      // Remove skeleton cards before adding real products
      if (skeletonContainer) {
        skeletonContainer.remove();
      }

      // Only append new products - no re-rendering of existing products
      const currentProductCount = currentProducts.length;
      let addedCount = 0;
      const fragment = document.createDocumentFragment();

      newProducts.forEach(product => {
        const handle = this.getProductHandle(product);
        if (handle && !currentProductHandles.includes(handle)) {
          const clonedProduct = product.cloneNode(true);

          // Debug: Log product details
          console.log('Adding product:', handle, clonedProduct.outerHTML.substring(0, 200) + '...');

          // Ensure the cloned product is properly structured
          if (clonedProduct.children.length === 0) {
            console.warn('Empty product detected, using original element');
            fragment.appendChild(product.cloneNode(true));
          } else {
            fragment.appendChild(clonedProduct);
          }
          addedCount++;
        }
      });

      if (addedCount > 0) {
        console.log(`Adding ${addedCount} new products to container`);

        // Simply append new products - no re-rendering
        container.appendChild(fragment);

        console.log(`Container now has ${container.children.length} total products`);

        // Ensure new products are visible and properly initialized
        this.ensureNewProductsVisible(container, currentProductCount);

        // Trigger minimal updates only for new products
        this.initializeNewProducts(container, currentProductCount);

        // Debug: Check if products are now visible
        setTimeout(() => {
          const newProductElements = Array.from(container.children).slice(currentProductCount);
          const visibleCount = newProductElements.filter(p => p.offsetHeight > 0).length;
          console.log(`${visibleCount} of ${newProductElements.length} new products are visible`);

          if (visibleCount === 0) {
            console.error('New products are not visible! Checking for issues...');
            newProductElements.forEach((product, index) => {
              const style = window.getComputedStyle(product);
              console.log(`Product ${index}:`, {
                display: style.display,
                visibility: style.visibility,
                opacity: style.opacity,
                height: product.offsetHeight,
                innerHTML: product.innerHTML.substring(0, 100) + '...'
              });
            });
          }
        }, 100);

        // Initialize new products
        this.initializeNewProducts(container, currentProductCount);
      }

      // Update or hide pagination
      const nextPagination = doc.querySelector('[data-load-more-pagination]');
      const currentPagination = document.querySelector('[data-load-more-pagination]');

      if (currentPagination && nextPagination) {
        currentPagination.innerHTML = nextPagination.innerHTML;
        currentPagination.style.opacity = '1';
        currentPagination.style.visibility = 'visible';

        // Re-setup buttons for the new pagination
        setTimeout(() => this.setupLoadMoreButtons(), 100);
      } else if (currentPagination) {
        // No more pages - hide the pagination completely
        currentPagination.style.display = 'none';
      }

      // Update URL without reload
      window.history.pushState({}, '', nextUrl);

    } catch (error) {
      console.error('Load More Error:', error);

      // Remove skeleton cards on error
      if (skeletonContainer) {
        skeletonContainer.remove();
      }

      // Show pagination container again
      const paginationContainer = document.querySelector('[data-load-more-pagination]');
      if (paginationContainer) {
        paginationContainer.style.opacity = '1';
        paginationContainer.style.visibility = 'visible';
      }

      button.textContent = 'Error - Try Again';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 3000);
    } finally {
      this.isLoading = false;
      button.textContent = originalText;
      button.disabled = false;

      // Ensure pagination is visible (fallback)
      const paginationContainer = document.querySelector('[data-load-more-pagination]');
      if (paginationContainer && (paginationContainer.style.opacity === '0' || paginationContainer.style.visibility === 'hidden')) {
        paginationContainer.style.opacity = '1';
        paginationContainer.style.visibility = 'visible';
      }
    }
  }

  ensureNewProductsVisible(container, startIndex) {
    const allChildren = Array.from(container.children);
    const newProducts = allChildren.slice(startIndex);

    // Check if new products are visible
    const invisibleProducts = newProducts.filter(product => {
      const style = window.getComputedStyle(product);
      return style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0' ||
        product.offsetHeight === 0;
    });

    if (invisibleProducts.length > 0) {
      console.log(`Found ${invisibleProducts.length} invisible products, fixing...`);

      // Fix visibility issues
      invisibleProducts.forEach(product => {
        product.style.display = '';
        product.style.visibility = 'visible';
        product.style.opacity = '1';

        // Remove any hidden classes
        product.classList.remove('hidden', 'invisible');

        // Force reflow
        product.offsetHeight;
      });
    }

    // Ensure all images load
    newProducts.forEach(product => {
      const images = product.querySelectorAll('img');
      images.forEach(img => {
        if (img.loading === 'lazy') {
          img.loading = 'eager';
        }
      });
    });
  }

  initializeNewProducts(container, startIndex) {
    // Only initialize new products starting from startIndex
    const allChildren = Array.from(container.children);
    const newProducts = allChildren.slice(startIndex);

    // 1. Initialize only new product cards
    newProducts.forEach(product => {
      if (product.tagName && product.tagName.toLowerCase() === 'product-card') {
        if (product.connectedCallback) {
          product.connectedCallback();
        }
      }

      // Initialize any nested product cards
      const nestedCards = product.querySelectorAll('product-card');
      nestedCards.forEach(card => {
        if (card.connectedCallback) {
          card.connectedCallback();
        }
      });
    });

    // 2. Dispatch events for new products only
    newProducts.forEach(product => {
      product.dispatchEvent(new CustomEvent('product:loaded', { bubbles: true }));
    });

    // 3. Trigger minimal container updates
    container.dispatchEvent(new CustomEvent('products:added', {
      bubbles: true,
      detail: { newProducts: newProducts, startIndex: startIndex }
    }));
  }

  triggerProductListUpdate(container) {
    // 1. Dispatch custom events
    container.dispatchEvent(new CustomEvent('products:added', { bubbles: true }));
    container.dispatchEvent(new Event('change', { bubbles: true }));

    // 2. Trigger Shopify theme events
    document.dispatchEvent(new CustomEvent('shopify:section:load'));

    // 3. If it's a web component, trigger updates
    if (container.requestUpdate) {
      container.requestUpdate();
    }
    if (container.connectedCallback) {
      container.connectedCallback();
    }

    // 4. Force style recalculation
    container.style.display = 'none';
    container.offsetHeight; // Force reflow
    container.style.display = '';

    // 5. Initialize any product cards
    const productCards = container.querySelectorAll('product-card');
    productCards.forEach(card => {
      if (card.connectedCallback) {
        card.connectedCallback();
      }
    });

    // 6. Fix any overflow issues
    this.ensureProductsVisible(container);
  }

  ensureProductsVisible(container) {
    // Fix container overflow
    container.style.overflow = 'visible';

    // Fix parent containers
    let parent = container.parentElement;
    while (parent && !parent.classList.contains('shopify-section')) {
      if (window.getComputedStyle(parent).overflow === 'hidden') {
        parent.style.overflow = 'visible';
      }
      parent = parent.parentElement;
    }
  }

  calculateStickyOffset() {
    let totalOffset = 0;

    // Common selectors for fixed/sticky elements
    const stickySelectors = [
      'header',
      '.header',
      '[data-header]',
      '.collection-toolbar',
      '.sticky-header',
      '.announcement-bar',
      '.site-header',
      '.shopify-section-header',
      'sticky-header' // Shopify web component
    ];

    stickySelectors.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        const style = window.getComputedStyle(element);
        const isSticky = style.position === 'fixed' || style.position === 'sticky';
        const isVisible = element.offsetHeight > 0 && style.display !== 'none';

        if (isSticky && isVisible) {
          totalOffset += element.offsetHeight;
        }
      }
    });

    // Add breathing room for better visual spacing
    totalOffset += 50;

    return totalOffset;
  }

  getCurrentProductHandles() {
    const container = this.findProductContainer();
    if (!container) return [];

    return Array.from(container.children)
      .filter(child => !child.classList.contains('skeleton-cards-container'))
      .map(product => this.getProductHandle(product))
      .filter(Boolean);
  }

  getProductHandle(product) {
    const link = product.querySelector('a[href*="/products/"]');
    if (link) {
      const href = link.getAttribute('href');
      const match = href.match(/\/products\/([^?#]+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  findProductContainer() {
    const selectors = [
      'product-list[id*="product-list"]',
      '.product-list',
      '[id*="product-list"]',
      '.product-grid',
      '.products'
    ];

    for (const selector of selectors) {
      const container = document.querySelector(selector);
      if (container) {
        return container;
      }
    }

    return null;
  }

  findProductContainerInDoc(doc) {
    // Same selectors but search in the response document
    const selectors = [
      'product-list[id*="product-list"]',
      '.product-list',
      '[id*="product-list"]',
      '.product-grid',
      '.products'
    ];

    for (const selector of selectors) {
      const container = doc.querySelector(selector);
      if (container && container.children.length > 0) {
        return container;
      }
    }

    return null;
  }

  styleLoadMoreButton(button) {
    if (!button.classList.contains('load-more-styled')) {
      button.style.display = 'block';
      button.style.margin = '2rem auto';
      button.style.padding = '12px 24px';
      button.style.minWidth = '160px';
      button.style.cursor = 'pointer';
      button.classList.add('load-more-styled');
    }
  }
}

// Initialize
window.shopifyLoadMoreHandler = new ShopifyLoadMoreHandler(); 